import { useEffect, useMemo, useState } from 'react'
import { api } from '../api'
import { navigate } from '../router'
import { appToday } from '../datetime'
import type { Category, Client, Entry, Todo } from '../../shared/types'
import { formatHours, formatHours2 } from '../../shared/duration'
import { formatCents } from '../../shared/money'
import { pushError } from '../toast'
import PunchClock from './PunchClock'

type RangeMode = 'today' | 'week' | 'month' | 'custom'

// ── date helpers (UTC, week starts Monday) ──
function today(): string {
  return appToday()
}
function mondayOf(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`)
  const dow = d.getUTCDay()
  d.setUTCDate(d.getUTCDate() + (dow === 0 ? -6 : 1 - dow))
  return d.toISOString().slice(0, 10)
}
function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}
function monthStart(iso: string): string {
  return iso.slice(0, 8) + '01'
}
function monthEnd(iso: string): string {
  const [y, m] = iso.split('-').map(Number)
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10)
}
function inclusiveDays(start: string, end: string): number {
  return Math.round((Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86400000) + 1
}
function fmtRangeLabel(start: string, end: string): string {
  const s = new Date(`${start}T00:00:00Z`)
  const e = new Date(`${end}T00:00:00Z`)
  const curY = new Date().getFullYear()
  const showYear = s.getUTCFullYear() !== curY || e.getUTCFullYear() !== curY
  const opt: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', timeZone: 'UTC' }
  const withYear: Intl.DateTimeFormatOptions = { ...opt, year: 'numeric' }
  const fmt = (d: Date) => d.toLocaleDateString('en-US', showYear ? withYear : opt)
  return start === end ? fmt(s) : `${fmt(s)} – ${fmt(e)}`
}

export default function Dashboard() {
  const [mode, setMode] = useState<RangeMode>('week')
  const [customStart, setCustomStart] = useState(today())
  const [customEnd, setCustomEnd] = useState(today())

  const range = useMemo(() => {
    const t = today()
    switch (mode) {
      case 'today':
        return { start: t, end: t }
      case 'week':
        return { start: mondayOf(t), end: addDays(mondayOf(t), 6) }
      case 'month':
        return { start: monthStart(t), end: monthEnd(t) }
      case 'custom':
        return { start: customStart, end: customEnd < customStart ? customStart : customEnd }
    }
  }, [mode, customStart, customEnd])

  const [clients, setClients] = useState<Client[]>([])
  const [allCats, setAllCats] = useState<Category[]>([])
  const [entries, setEntries] = useState<Entry[]>([])
  const [todos, setTodos] = useState<Todo[]>([])
  const [loading, setLoading] = useState(true)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    setLoading(true)
    ;(async () => {
      try {
        const [cl, cats, es, td] = await Promise.all([
          api.listClients(false),
          api.listAllCategories(),
          api.listEntriesByRange(range.start, range.end),
          api.listTodos(),
        ])
        setClients(cl)
        setAllCats(cats)
        setEntries(es)
        setTodos(td)
      } catch (e) {
        pushError(`Couldn't load dashboard: ${(e as Error).message}`)
      } finally {
        setLoading(false)
      }
    })()
  }, [range.start, range.end, reloadKey])

  const clientById = useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients])

  const clientRollup = useMemo(() => {
    const factor = inclusiveDays(range.start, range.end) / 7
    const showTargets = mode === 'week' || mode === 'month'
    const map = new Map<number, { minutes: number; cents: number }>()
    for (const r of entries) {
      if (r.client_id == null) continue
      const g = map.get(r.client_id) ?? { minutes: 0, cents: 0 }
      g.minutes += r.duration_minutes
      const cl = clientById.get(r.client_id)
      const cat = r.category_id != null ? allCats.find((c) => c.id === r.category_id) : undefined
      const rate = cat?.rate_cents != null ? cat.rate_cents : cl?.default_rate_cents ?? 0
      g.cents += Math.round((r.duration_minutes * rate) / 60)
      map.set(r.client_id, g)
    }
    const items = [...map.entries()]
      .map(([cid, g]) => {
        const cl = clientById.get(cid)
        const min = cl?.target_weekly_min_minutes != null ? cl.target_weekly_min_minutes * factor : null
        const max = cl?.target_weekly_max_minutes != null ? cl.target_weekly_max_minutes * factor : null
        let status: 'under' | 'in' | 'over' | null = null
        if (showTargets && (min != null || max != null)) {
          if (min != null && g.minutes < min) status = 'under'
          else if (max != null && g.minutes > max) status = 'over'
          else status = 'in'
        }
        return { client_id: cid, name: cl?.simple_name ?? 'Unknown', accent: cl?.accent_color ?? '#3b6ea5', minutes: g.minutes, cents: g.cents, status, bandMin: min, bandMax: max }
      })
      .sort((a, b) => b.minutes - a.minutes)
    return { items, totalMin: items.reduce((s, i) => s + i.minutes, 0), totalCents: items.reduce((s, i) => s + i.cents, 0), showTargets }
  }, [entries, clientById, allCats, range.start, range.end, mode])

  const minutesByClient = useMemo(() => new Map(clientRollup.items.map((i) => [i.client_id, i.minutes])), [clientRollup])

  return (
    <div className="mx-auto max-w-6xl px-4 py-5 sm:px-6 lg:px-8">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Dashboard</h1>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-800"
            value={mode}
            onChange={(e) => setMode(e.target.value as RangeMode)}
          >
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="custom">Custom Range</option>
          </select>
          {mode === 'custom' && (
            <div className="flex items-center gap-1 text-sm">
              <CommitDateInput value={range.start} onCommit={setCustomStart} />
              <span className="text-slate-400">→</span>
              <CommitDateInput value={range.end} onCommit={setCustomEnd} />
            </div>
          )}
          <span className="text-sm font-medium text-slate-600">{fmtRangeLabel(range.start, range.end)}</span>
        </div>
      </header>

      {loading ? (
        <div className="py-10 text-slate-400">Loading…</div>
      ) : (
        <>
          {/* Punch clock */}
          <section className="mb-6">
            <PunchClock clients={clients} allCats={allCats} onLogged={() => setReloadKey((k) => k + 1)} />
          </section>

          {/* Client quick-launch */}
          <section className="mb-8">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Clients</h2>
            {clients.length === 0 ? (
              <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
                No clients yet.{' '}
                <button className="font-medium text-[var(--accent)] underline" onClick={() => navigate('#/client/new')}>
                  Add one
                </button>
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {clients.map((c) => {
                  const accent = c.accent_color ?? '#3b6ea5'
                  const mins = minutesByClient.get(c.id) ?? 0
                  return (
                    <button
                      key={c.id}
                      onClick={() => navigate(`#/client/${c.id}`)}
                      className="group flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 text-left transition hover:-translate-y-0.5 hover:shadow-md"
                      style={{ borderLeftColor: accent, borderLeftWidth: 4 }}
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white" style={{ backgroundColor: accent }}>
                        {c.simple_name.slice(0, 1).toUpperCase()}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-slate-900">{c.simple_name}</span>
                        <span className="tabular block text-xs text-slate-500">{formatHours2(mins)} h this range</span>
                      </span>
                    </button>
                  )
                })}
              </div>
            )}
          </section>

          {/* Widgets */}
          <section className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Hours by client</h2>
              <ClientWidget rollup={clientRollup} />
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Top to-dos</h2>
                <button className="text-xs text-[var(--accent)] hover:underline" onClick={() => navigate('#/todos')}>
                  View all
                </button>
              </div>
              <TodoWidget todos={todos} clientById={clientById} />
            </div>
          </section>
        </>
      )}
    </div>
  )
}

function CommitDateInput({ value, onCommit }: { value: string; onCommit: (v: string) => void }) {
  const [v, setV] = useState(value)
  useEffect(() => setV(value), [value])
  return (
    <input
      type="date"
      className="tabular rounded border border-slate-300 px-2 py-1"
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => v && v !== value && onCommit(v)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
      }}
    />
  )
}

interface RollupData {
  items: { client_id: number; name: string; accent: string; minutes: number; cents: number; status: 'under' | 'in' | 'over' | null; bandMin: number | null; bandMax: number | null }[]
  totalMin: number
  totalCents: number
  showTargets: boolean
}

function ClientWidget({ rollup }: { rollup: RollupData }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <th className="px-3 py-2">Client</th>
            <th className="px-3 py-2 text-right">Hours</th>
            <th className="px-3 py-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {rollup.items.length === 0 && (
            <tr>
              <td colSpan={3} className="px-3 py-4 text-center text-slate-400">
                No entries in this range.
              </td>
            </tr>
          )}
          {rollup.items.map((it) => (
            <tr key={it.client_id} className="border-b border-slate-100 align-top">
              <td className="px-3 py-2">
                <button className="flex items-center gap-2 text-left hover:underline" onClick={() => navigate(`#/client/${it.client_id}`)} title="Open client">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: it.accent }} />
                  <span className="font-medium text-slate-800">{it.name}</span>
                </button>
                {it.status && <TargetPill status={it.status} bandMin={it.bandMin} bandMax={it.bandMax} />}
              </td>
              <td className="px-3 py-2 text-right tabular text-slate-700">{formatHours2(it.minutes)}</td>
              <td className="px-3 py-2 text-right tabular text-slate-800">{formatCents(it.cents)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-slate-300 font-semibold">
            <td className="px-3 py-2 text-slate-900">Total</td>
            <td className="px-3 py-2 text-right tabular text-slate-900">{formatHours2(rollup.totalMin)}</td>
            <td className="px-3 py-2 text-right tabular text-slate-900">{formatCents(rollup.totalCents)}</td>
          </tr>
        </tfoot>
      </table>
      {!rollup.showTargets && (
        <p className="border-t border-slate-100 px-3 py-2 text-xs text-slate-400">Target status shows in Week and Month views.</p>
      )}
    </div>
  )
}

function TodoWidget({ todos, clientById }: { todos: Todo[]; clientById: Map<number, Client> }) {
  const top = todos.slice(0, 8)
  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      {top.length === 0 ? (
        <p className="px-3 py-4 text-center text-sm text-slate-400">No open to-dos.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-slate-100">
          {top.map((t) => {
            const accent = t.client_id != null ? clientById.get(t.client_id)?.accent_color ?? '#94a3b8' : '#cbd5e1'
            return (
              <li key={t.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: accent }} />
                <span className="truncate text-slate-700">{t.task || '(untitled task)'}</span>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function TargetPill({ status, bandMin, bandMax }: { status: 'under' | 'in' | 'over'; bandMin: number | null; bandMax: number | null }) {
  const cfg = {
    under: { cls: 'bg-amber-100 text-amber-800', label: 'Under target' },
    in: { cls: 'bg-green-100 text-green-800', label: 'In target range' },
    over: { cls: 'bg-red-100 text-red-800', label: 'Over target' },
  }[status]
  const band =
    bandMin != null && bandMax != null
      ? `${formatHours(bandMin)}–${formatHours(bandMax)}h`
      : bandMin != null
        ? `≥ ${formatHours(bandMin)}h`
        : bandMax != null
          ? `≤ ${formatHours(bandMax)}h`
          : ''
  return (
    <div className={'mt-1 inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ' + cfg.cls}>
      {cfg.label}
      {band && <span className="ml-1 font-normal opacity-80">({band})</span>}
    </div>
  )
}
